import { Modal, RadioList, RadioListItem } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useVariantsSelector } from '../../../../state/SplitTests/selectors/useVariantsSelector';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useConvertSplitTestToMessage } from '../../DisplayWebContent/hooks/useConvertSplitTestToMessage';
import { useDeleteAllVariants } from '../../DisplayWebContent/hooks/useDeleteAllVariants';

export interface RemoveSplitTestModalProps {
  isOpen: boolean;
  onClose(): void;
  splitTestId: string;
}

const SubHeader = styled.div`
  font: ${({ theme }) => theme.designToken.body1};
  color: ${({ theme }) => theme.designToken.neutral900};
  padding: 0 0 ${({ theme }) => theme.designToken.spacing4} 0;
`;

const DELETE_ALL_VARIANTS_OPTIONS = 'remove-all';

const RemoveVariantOption = ({ messageId, name, onSelected, currentSelected }) => {
  const { t } = useTranslation('campaigns');

  const handleOnSelected = useCallback(() => {
    onSelected(messageId);
  }, [messageId, onSelected]);

  const isSelected = useMemo(() => currentSelected && currentSelected === messageId, [currentSelected, messageId]);

  return (
    <RadioListItem
      id={`radio-list-item-${messageId}`}
      onClick={handleOnSelected}
      selected={isSelected}
      aria-label={t('onsite.editor.workflow.splitTest.workFlowModalLabelForDeletVariant', {
        name: name,
      })}
      heading={t('onsite.editor.workflow.splitTest.workFlowModalLabelForDeletVariant', {
        name: name,
      })}
    />
  );
};

const RemoveSplitTestOption = ({ onSelected, currentSelected }) => {
  const { t } = useTranslation('campaigns');

  const handleOnSelected = useCallback(() => {
    onSelected(DELETE_ALL_VARIANTS_OPTIONS);
  }, [onSelected]);

  const isSelected = useMemo(() => currentSelected && currentSelected === DELETE_ALL_VARIANTS_OPTIONS, [
    currentSelected,
  ]);

  return (
    <RadioListItem
      heading={t('onsite.editor.workflow.splitTest.workFlowModalLabelForDeletAllVariant')}
      id="delete-all-variant"
      onClick={handleOnSelected}
      selected={isSelected}
    />
  );
};

export const RemoveSplitTestModal = ({ isOpen, onClose, splitTestId }: RemoveSplitTestModalProps) => {
  const { t } = useTranslation('campaigns');

  const variants = useVariantsSelector(splitTestId);
  const deleteAllVariants = useDeleteAllVariants();
  const convertSplitTestToMessage = useConvertSplitTestToMessage();

  /**
   * The user can choose to keep one variant or remove the split test entirely.
   *
   * Either the variant's ID or `DELETE_ALL_VARIANTS_OPTIONS` will be stored
   * when an option has been selected.
   */
  const [selectedRemoveOption, setSelectedRemoveOption] = useState<string>();

  const onRemoveSplitTest = useCallback(() => {
    if (selectedRemoveOption === DELETE_ALL_VARIANTS_OPTIONS) {
      deleteAllVariants({ splitTestId });
    } else {
      convertSplitTestToMessage({ splitTestId, messageToKeepId: selectedRemoveOption });
    }

    onClose();
  }, [convertSplitTestToMessage, deleteAllVariants, onClose, selectedRemoveOption, splitTestId]);

  return (
    <Modal
      heading={t('onsite.editor.workflow.splitTest.workFlowModalTitleForDeletion')}
      variation="clean"
      isOpen={isOpen}
      okButtonLabel={'Continue'}
      okButtonAriaLabel={t('onsite.editor.workflow.splitTest.workFlowModalConfirmRemoveSplitTest')}
      okButtonDisabled={!selectedRemoveOption}
      onCloseButtonClicked={onClose}
      onOkButtonClicked={onRemoveSplitTest}
      minWidth={630}
    >
      <SubHeader>{t('onsite.editor.workflow.splitTest.workFlowModalSubTitleForDeletion')}</SubHeader>

      <RadioList compacted>
        {variants.map(s => {
          return (
            <RemoveVariantOption
              key={s.messageId}
              messageId={s.messageId}
              name={s.name}
              onSelected={setSelectedRemoveOption}
              currentSelected={selectedRemoveOption}
            />
          );
        })}

        <RemoveSplitTestOption onSelected={setSelectedRemoveOption} currentSelected={selectedRemoveOption} />
      </RadioList>
    </Modal>
  );
};
